import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { customerService } from '../../services/customerService';
import { ActiveCustomer, UnpaidInvoice, AgentInvoiceAnalysis } from '../../types/organizations';
import { getCategoryStyling } from '../../utils/categoryStyling';
import { authFetch } from '../../utils/authFetch';
import { useUser } from '../../context/UserContext';
import CustomerInvoicesModal from '../common/CustomerInvoicesModal';
import RiskProfileModal from '../common/modals/RiskProfileModal';
import InvoiceCardModal from '../common/modals/InvoiceCardModal';
import DataCard from '../common/DataCard';

interface CustomerRubrics {
  ltm_invoices: string;
  receivable: number;
  ltm_value: number;
  first_order_date: string | null;
  first_order_days_ago: string | null;
  dpd: string;
  otpr: string;
  dbt: string;
  ptp?: string;
  credit_risk_profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning';
  credit_risk_profile_reasonings?: string[];
}

interface CustomerRow {
  id: string;
  name: string;
  xero_contact_id: string;
  invoice_metadata: {
    outstanding_invoice_count: number;
    last_12m_invoice_count: number;
    total_receivable: number;
    last_12m_value: number;
    overdue_invoice_count: number;
    overdue_receivable: number;
    total_invoice_count: number;
    lifetime_value: number;
  };
  rubrics: CustomerRubrics;
  ap_archetype?: {
    id: string;
    name: string;
    description?: string;
    descriptions?: Array<{ key: string; value: any; is_active?: boolean }>;
  };
  governance_overlay?: {
    id: string;
    name: string;
    description?: string;
    descriptions?: Array<{ key: string; value: any; is_active?: boolean }>;
  };
  note_count?: number;
}

interface SortConfig { field: string; direction: 'asc' | 'desc'; }

interface UnderstandRubricsStepProps {
  organizationId: string;
  customers: CustomerRow[];
  customersInSections?: CustomerInSection[];
  governanceOverlays?: Array<{ id: string; name: string; description?: string }>;
  publicMode?: boolean;
}

interface CustomerInSection {
  id: string;
  customerId: string;
  name: string;
  sectionType: 'ap_archetype' | 'governance_overlay' | 'custom_behavior';
  sectionId: string;
  isDuplicated: boolean;
}

const UnderstandRubricsStep: React.FC<UnderstandRubricsStepProps> = ({ organizationId, customers, customersInSections = [], governanceOverlays = [], publicMode = false }) => {
  const { accessToken, setUserAndToken } = useUser();
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([{ field: 'name', direction: 'asc' }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [riskFilter, setRiskFilter] = useState<'All' | 'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning'>('All');
  
  // Modal states
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [modalReasonings, setModalReasonings] = useState<string[]>([]);
  const [modalProfile, setModalProfile] = useState<'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning' | undefined>(undefined);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [selectedCustomerForInvoices, setSelectedCustomerForInvoices] = useState<CustomerRow | null>(null);
  const [invoicesCache, setInvoicesCache] = useState<Record<string, any[]>>({});
  const [showInvoiceCardModal, setShowInvoiceCardModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<UnpaidInvoice | null>(null);
  const [showArchetypeModal, setShowArchetypeModal] = useState(false);
  const [selectedArchetype, setSelectedArchetype] = useState<any>(null);
  const [showGovernanceModal, setShowGovernanceModal] = useState(false);
  const [selectedGovernance, setSelectedGovernance] = useState<any>(null);
  const [archetypeCache, setArchetypeCache] = useState<Record<string, any>>({});
  const [governanceCache, setGovernanceCache] = useState<Record<string, any>>({});
  const loadingArchetypesRef = useRef<Set<string>>(new Set());
  const loadingGovernancesRef = useRef<Set<string>>(new Set());


  const sortCustomers = (rows: CustomerRow[], config: SortConfig[]) => {
    if (config.length === 0) return rows;
    return [...rows].sort((a, b) => {
      for (const s of config) {
        let av: any; let bv: any;
        switch (s.field) {
          case 'name':
            av = a.name.toLowerCase();
            bv = b.name.toLowerCase();
            break;
          case 'ltm_invoices': {
            const aL = a.rubrics?.ltm_invoices || '0/0';
            const bL = b.rubrics?.ltm_invoices || '0/0';
            av = parseInt(aL.split('/')[0]) || 0;
            bv = parseInt(bL.split('/')[0]) || 0;
            break;
          }
          case 'receivable':
            av = a.rubrics?.receivable || 0;
            bv = b.rubrics?.receivable || 0;
            break;
          case 'ltm_value':
            av = a.rubrics?.ltm_value || 0;
            bv = b.rubrics?.ltm_value || 0;
            break;
          case 'otpr':
            av = parseFloat((a.rubrics?.otpr || '0').toString().replace('%', '')) || 0;
            bv = parseFloat((b.rubrics?.otpr || '0').toString().replace('%', '')) || 0;
            break;
          case 'dbt':
            av = parseFloat((a.rubrics?.dbt || '0').toString()) || 0;
            bv = parseFloat((b.rubrics?.dbt || '0').toString()) || 0;
            break;
          default:
            return 0;
        }
        if (av < bv) return s.direction === 'asc' ? -1 : 1;
        if (av > bv) return s.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const filtered = useMemo(() => {
    if (!customers || !Array.isArray(customers)) return [];
    return riskFilter === 'All' ? customers : customers.filter(c => (c.rubrics?.credit_risk_profile || '') === riskFilter);
  }, [customers, riskFilter]);

  const sorted = useMemo(() => {
    if (!filtered || !Array.isArray(filtered)) return [];
    return sortCustomers(filtered, sortConfig);
  }, [filtered, sortConfig]);

  useEffect(() => { setCurrentPage(1); }, [riskFilter]);

  const startIndex = (currentPage - 1) * pageSize;
  const displayed = sorted?.slice(startIndex, startIndex + pageSize) || [];
  const totalPages = Math.ceil((sorted?.length || 0) / pageSize) || 1;

  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  const getSortIcon = (field: string) => {
    const s = sortConfig.find(sc => sc.field === field);
    if (!s) return null;
    return s.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };
  const handleSort = (field: string) => {
    const existing = sortConfig.find(s => s.field === field);
    let cfg: SortConfig[];
    if (existing) {
      if (existing.direction === 'asc') { existing.direction = 'desc'; cfg = [...sortConfig]; }
      else { cfg = sortConfig.filter(s => s.field !== field); }
    } else { cfg = [...sortConfig, { field, direction: 'asc' }]; }
    setSortConfig(cfg);
    setCurrentPage(1);
  };

  // Risk profile styling
  const riskChipClasses = (profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN') => {
    if (!profile) return 'bg-gray-100 text-gray-800 border border-gray-200';
    
    const styling = getCategoryStyling(profile);
    return `${styling.bg} ${styling.text} border border-gray-200`;
  };

  // Get all governance overlays for a customer
  const getCustomerGovernanceOverlays = (customerId: string): Array<{ id: string; name: string; description?: string; isDuplicated: boolean }> => {
    const customerInSections = customersInSections.filter(
      cis => cis.customerId === customerId && cis.sectionType === 'governance_overlay'
    );
    
    return customerInSections.map(cis => {
      const overlay = governanceOverlays.find(go => go.id === cis.sectionId);
      return overlay ? { ...overlay, isDuplicated: cis.isDuplicated } : null;
    }).filter((overlay): overlay is { id: string; name: string; description?: string; isDuplicated: boolean } => overlay !== null);
  };

  // Modal handlers
  const openRiskModal = (profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN', reasonings?: string[]) => {
    setModalProfile(profile);
    const order = [
      /^NEW/i, /^Not NEW/i,
      /^RED/i, /^Not RED/i,
      /^AMBER/i, /^Not AMBER/i,
      /^GREEN/i, /^Not GREEN/i
    ];
    const items = [...(reasonings || [])];
    const ranked = items.sort((a, b) => {
      const ai = order.findIndex((re) => re.test(a));
      const bi = order.findIndex((re) => re.test(b));
      const aIdx = ai === -1 ? 999 : ai;
      const bIdx = bi === -1 ? 999 : bi;
      if (aIdx !== bIdx) return aIdx - bIdx;
      return a.localeCompare(b);
    });
    setModalReasonings(ranked);
    setShowRiskModal(true);
  };

  const handleOpenInvoicesModal = async (customer: CustomerRow) => {
    // Check if invoices are already cached
    if (!invoicesCache[customer.xero_contact_id]) {
      try {
        const result = await customerService.getCustomerInvoices(
          organizationId,
          customer.xero_contact_id,
          accessToken,
          setUserAndToken
        );
        setInvoicesCache(prev => ({
          ...prev,
          [customer.xero_contact_id]: result.invoices || []
        }));
      } catch (err: any) {
        console.error('Error fetching invoices:', err);
        // Still open modal even if fetch fails
      }
    }

    setSelectedCustomerForInvoices(customer);
    setShowInvoicesModal(true);
  };

  const handleCloseInvoicesModal = () => {
    setShowInvoicesModal(false);
    setSelectedCustomerForInvoices(null);
  };

  const handleInvoiceClick = async (invoiceNumber: string) => {
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/organizations/${organizationId}/invoices/${invoiceNumber}`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const responseData = await response.json();
        setSelectedInvoice(responseData.data.invoice);
        setShowInvoiceCardModal(true);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
    }
  };

  const handleArchetypeClick = async (archetype: any) => {
    // Check if we have detailed data in cache
    if (archetypeCache[archetype.id]) {
      setSelectedArchetype(archetypeCache[archetype.id]);
      setShowArchetypeModal(true);
      return;
    }

    // Check if we're already loading this archetype
    if (loadingArchetypesRef.current.has(archetype.id)) {
      return;
    }

    // Mark as loading immediately to prevent race conditions
    loadingArchetypesRef.current.add(archetype.id);

    // Fetch detailed data from API
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/ap-archetypes/${archetype.id}`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const responseData = await response.json();
        // The API returns the data directly, not wrapped in a 'data' field
        const detailedArchetype = responseData;
        
        // Cache the detailed data
        setArchetypeCache(prev => ({
          ...prev,
          [archetype.id]: detailedArchetype
        }));
        
        // Show modal with detailed data
        setSelectedArchetype(detailedArchetype);
        setShowArchetypeModal(true);
      } else {
        // Show modal with basic data as fallback
        setSelectedArchetype(archetype);
        setShowArchetypeModal(true);
      }
    } catch (err) {
      console.error('Error fetching archetype details:', err);
      // Show modal with basic data as fallback
      setSelectedArchetype(archetype);
      setShowArchetypeModal(true);
    } finally {
      // Remove from loading set
      loadingArchetypesRef.current.delete(archetype.id);
    }
  };

  const handleGovernanceClick = async (governance: any) => {
    // Check if we have detailed data in cache
    if (governanceCache[governance.id]) {
      setSelectedGovernance(governanceCache[governance.id]);
      setShowGovernanceModal(true);
      return;
    }

    // Check if we're already loading this governance
    if (loadingGovernancesRef.current.has(governance.id)) {
      return;
    }

    // Mark as loading immediately to prevent race conditions
    loadingGovernancesRef.current.add(governance.id);

    // Fetch detailed data from API
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/governance-overlays/${governance.id}`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const responseData = await response.json();
        // The API returns the data directly, not wrapped in a 'data' field
        const detailedGovernance = responseData;
        
        // Cache the detailed data
        setGovernanceCache(prev => ({
          ...prev,
          [governance.id]: detailedGovernance
        }));
        
        // Show modal with detailed data
        setSelectedGovernance(detailedGovernance);
        setShowGovernanceModal(true);
      } else {
        // Show modal with basic data as fallback
        setSelectedGovernance(governance);
        setShowGovernanceModal(true);
      }
    } catch (err) {
      console.error('Error fetching governance overlay details:', err);
      // Show modal with basic data as fallback
      setSelectedGovernance(governance);
      setShowGovernanceModal(true);
    } finally {
      // Remove from loading set
      loadingGovernancesRef.current.delete(governance.id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <div className="flex items-end justify-between mb-6">
        <div className="flex items-end">
          <div className="flex items-center justify-center w-12 h-12 rounded-full text-2xl mr-4 bg-blue-100 text-blue-600">📊</div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Credit Controlling Rubrics</h3>
            <p className="text-sm sm:text-base text-gray-600">90 days rolling period.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
          <label className="text-xs sm:text-sm text-gray-600">Risk Profile:</label>
          <div className="relative">
            <select
              value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value as any)}
              className="text-xs sm:text-sm border border-gray-300 rounded-md px-2 py-1 bg-white appearance-none pr-6 sm:pr-8 cursor-pointer"
            >
              <option value="All">All</option>
              <option value="NEW">NEW</option>
              <option value="RED">RED</option>
              <option value="AMBER">AMBER</option>
              <option value="GREEN">GREEN</option>
              <option value="No Dunning">No Dunning</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 min-w-[162px] sm:w-65" onClick={() => handleSort('name')}>
                <div className="flex items-center">Customer Name {getSortIcon('name')}</div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 min-w-[80px] sm:w-24" onClick={() => handleSort('ltm_invoices')}>
                <div className="flex items-center">LTM Invoices {getSortIcon('ltm_invoices')}</div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 min-w-[90px] sm:w-28" onClick={() => handleSort('receivable')}>
                <div className="flex items-center">Receivable {getSortIcon('receivable')}</div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 min-w-[90px] sm:w-28" onClick={() => handleSort('ltm_value')}>
                <div className="flex items-center">LTM Value {getSortIcon('ltm_value')}</div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[203px] sm:w-81">Days Past Due (DPD)</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 min-w-[80px] sm:w-32" onClick={() => handleSort('otpr')}>
                <div className="flex items-center">OTPR {getSortIcon('otpr')}</div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 min-w-[80px] sm:w-40" onClick={() => handleSort('dbt')}>
                <div className="flex items-center">DBT {getSortIcon('dbt')}</div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] sm:w-36">Risk Profile</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] sm:w-32">AP Archetype</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] sm:w-32">Governance</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayed.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  {publicMode ? (
                    <span className="font-medium text-gray-900 text-xs sm:text-sm">{c.name}</span>
                  ) : (
                    <button
                      onClick={() => window.open(`/organizations/${organizationId}/customers/${c.id}`, '_blank')}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium block w-full text-left text-xs sm:text-sm"
                    >
                      {c.name}
                    </button>
                  )}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                  {publicMode ? (
                    <span className="text-gray-900">{c.rubrics?.ltm_invoices || 'N/A'}</span>
                  ) : (
                    <button
                      onClick={() => handleOpenInvoicesModal(c)}
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                    >
                      {c.rubrics?.ltm_invoices || 'N/A'}
                    </button>
                  )}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">{formatCurrency(c.rubrics?.receivable || 0)}</td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">{formatCurrency(c.rubrics?.ltm_value || 0)}</td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900">
                  <div>
                    {(c.rubrics?.dpd || 'N/A').split(/(\(\w+-\d+\))/g).map((part, index) => {
                      if (part.match(/\(\w+-\d+\)/)) {
                        // Extract invoice number from parentheses
                        const invoiceNumber = part.replace(/[()]/g, '');
                        return publicMode ? (
                          <span key={index} className="text-gray-900">{part}</span>
                        ) : (
                          <button
                            key={index}
                            onClick={() => handleInvoiceClick(invoiceNumber)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {part}
                          </button>
                        );
                      }
                      return (
                        <span key={index}>
                          {part.split('<br />').map((line, lineIndex, array) => (
                            <React.Fragment key={lineIndex}>
                              {line}
                              {lineIndex < array.length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">{c.rubrics?.otpr || 'N/A'}</td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">{c.rubrics?.dbt || 'N/A'}</td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      if (c.rubrics?.credit_risk_profile === 'No Dunning') {
                        // Handle dunning modal if needed
                        openRiskModal(undefined, c.rubrics?.credit_risk_profile_reasonings);
                      } else {
                        openRiskModal(c.rubrics?.credit_risk_profile, c.rubrics?.credit_risk_profile_reasonings);
                      }
                    }}
                    className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-semibold ${
                      c.rubrics?.credit_risk_profile === 'No Dunning'
                        ? 'bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200'
                        : riskChipClasses(c.rubrics?.credit_risk_profile)
                    }`}
                    title={c.rubrics?.credit_risk_profile === 'No Dunning' ? "View dunning reasonings" : "View reasonings"}
                  >
                    {c.rubrics?.credit_risk_profile || 'N/A'}
                  </button>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                  <div className="flex flex-wrap gap-1">
                    {c.ap_archetype ? (
                      <button
                        onClick={() => handleArchetypeClick(c.ap_archetype)}
                        className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full hover:bg-blue-200 transition-colors"
                      >
                        {c.ap_archetype?.name}
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">N/A</span>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                  <div className="flex flex-wrap gap-1">
                    {(() => {
                      const customerOverlays = getCustomerGovernanceOverlays(c.id);
                      if (customerOverlays.length === 0) {
                        return <span className="text-gray-400 text-xs">N/A</span>;
                      }
                      return customerOverlays.map((overlay, index) => (
                        <button
                          key={`${overlay.id}-${index}`}
                          onClick={() => handleGovernanceClick(overlay)}
                          className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full hover:bg-green-200 transition-colors ${
                            'bg-green-100 text-green-800'
                          }`}
                          title={overlay.isDuplicated ? 'Duplicated assignment' : ''}
                        >
                          {overlay.name}
                        </button>
                      ));
                    })()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-4 sm:mt-6 space-y-2 sm:space-y-0">
          <div className="text-xs sm:text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(startIndex + pageSize, sorted.length)} of {sorted.length} customers
          </div>
          <div className="flex space-x-1 sm:space-x-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 3).map(num => (
              <button key={num} onClick={() => setCurrentPage(num)} className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md ${currentPage === num ? 'bg-blue-600 text-white' : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'}`}>{num}</button>
            ))}
            {totalPages > 3 && (
              <>
                <span className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-500">...</span>
                <button onClick={() => setCurrentPage(totalPages)} className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">{totalPages}</button>
              </>
            )}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      {/* Risk Profile Modal */}
      <RiskProfileModal
        isOpen={showRiskModal}
        onClose={() => setShowRiskModal(false)}
        profile={modalProfile}
        reasonings={modalReasonings}
        organizationId={organizationId}
      />

      {/* Customer Invoices Modal */}
      {selectedCustomerForInvoices && (
        <CustomerInvoicesModal
          customer={{
            id: selectedCustomerForInvoices.id,
            name: selectedCustomerForInvoices.name,
            xero_contact_id: selectedCustomerForInvoices.xero_contact_id,
            contact_status: 'ACTIVE',
            addresses: [],
            phones: [],
            invoice_metadata: selectedCustomerForInvoices.invoice_metadata,
            agent_analyses: [],
            selected_category: undefined,
            customer_profile_summary: undefined
          } as ActiveCustomer}
          organizationId={organizationId}
          isOpen={showInvoicesModal}
          onClose={handleCloseInvoicesModal}
          invoices={invoicesCache[selectedCustomerForInvoices.xero_contact_id] || []}
        />
      )}

      {/* Invoice Card Modal */}
      <InvoiceCardModal
        isOpen={showInvoiceCardModal}
        onClose={() => setShowInvoiceCardModal(false)}
        organizationId={organizationId}
        invoice={selectedInvoice}
      />

      {/* AP Archetype Modal */}
      {showArchetypeModal && selectedArchetype && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black opacity-30" 
            onClick={(e) => {
              e.stopPropagation();
              setShowArchetypeModal(false);
            }}
          ></div>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">AP Archetype Details</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowArchetypeModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <DataCard
                data={selectedArchetype}
                dataType="apArchetype"
                showLink={false}
                compact={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Governance Overlay Modal */}
      {showGovernanceModal && selectedGovernance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black opacity-30" 
            onClick={(e) => {
              e.stopPropagation();
              setShowGovernanceModal(false);
            }}
          ></div>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Governance Overlay Details</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowGovernanceModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <DataCard
                data={selectedGovernance}
                dataType="governanceOverlay"
                showLink={false}
                compact={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnderstandRubricsStep;


